// File: src/components/HomePage.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../Header';
import MobileMenu from '../MobileMenu';
import MobileHeader from '../MobileHeader';
import Footer from '../Footer';
import ServicesComponent from './Services';
import BenefitsComponent from './Benefits';
import FloatingMenu from '../FloatingMenu';
import Presentation from './Presentation';
import NewsletterSignup from '../NewsletterSignup';
import SEO from '../SEO';
import apiClient from '../../api/apiClient';
import {
  getAccessToken,
  getRefreshToken,
  removeTokens,
  setAccessToken,
} from '../../api/authUtils';
import { jwtDecode } from 'jwt-decode'; // Corretto l'import
import './styles/HomePage.css';

const HomePage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFloatingMenu, setShowFloatingMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      if (accessToken) {
        try {
          const decodedAccessToken = jwtDecode(accessToken);
          const currentTime = Date.now() / 1000;

          if (decodedAccessToken.exp && decodedAccessToken.exp > currentTime) {
            setIsLoggedIn(true);
          } else if (refreshToken) {
            try {
              const response = await apiClient.post('/accounts/token/refresh/', {
                refresh: refreshToken,
              });

              if (response.status === 200) {
                const data = response.data;
                setAccessToken(data.access);
                setIsLoggedIn(true);
              } else {
                removeTokens();
                setIsLoggedIn(false);
              }
            } catch (error) {
              console.error('Errore durante il refresh del token:', error);
              removeTokens();
              setIsLoggedIn(false);
            }
          } else {
            removeTokens();
            setIsLoggedIn(false);
          }
        } catch (error) {
          console.error('Errore durante la decodifica del token di accesso:', error);
          removeTokens();
          setIsLoggedIn(false);
        }
      } else if (refreshToken) {
        try {
          const response = await apiClient.post('/accounts/token/refresh/', {
            refresh: refreshToken,
          });

          if (response.status === 200) {
            const data = response.data;
            setAccessToken(data.access);
            setIsLoggedIn(true);
          } else {
            removeTokens();
            setIsLoggedIn(false);
          }
        } catch (error) {
          console.error('Errore durante il refresh del token:', error);
          removeTokens();
          setIsLoggedIn(false);
        }
      } else {
        setIsLoggedIn(false);
      }
    };

    checkAuthStatus();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowFloatingMenu(true);
      } else {
        setShowFloatingMenu(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  return (
    <div className="homepage">
      <SEO 
        title="Minerva AI - Intelligenza Artificiale per Insegnanti: Lezioni Personalizzate e Coinvolgenti"
        description="Minerva AI è la piattaforma di intelligenza artificiale per insegnanti che ti aiuta a creare piani di lezione unici, attività coinvolgenti e personalizzare l'apprendimento per ogni studente. Risparmia tempo e stimola la curiosità in classe."
        keywords="intelligenza artificiale, educazione, didattica, insegnamento, AI scuola, minerva, AI scuola primaria, istruzione, pei, pdp"
        ogUrl="https://minervaeducationai.it/"
        canonical="https://minervaeducationai.it/"
      />
      
      {/* Header desktop */}
      <Header
        isLoggedIn={isLoggedIn}
        setIsLoggedIn={setIsLoggedIn}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {/* Header mobile */}
      <MobileHeader 
        isLoggedIn={isLoggedIn} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />

      {mobileMenuOpen && (
        <MobileMenu
          isLoggedIn={isLoggedIn}
          setIsLoggedIn={setIsLoggedIn}
          setMobileMenuOpen={setMobileMenuOpen}
        />
      )}

      {showFloatingMenu && (
        <FloatingMenu
          isLoggedIn={isLoggedIn}
          handleLogout={() => {
            removeTokens();
            setIsLoggedIn(false);
          }}
        />
      )}

      <div className="container">
        <main>
          {/* Sezione 1 */}
          <motion.section
            className="section-one"
            initial="hidden"
            animate="visible"
            variants={sectionVariants}
          >
            <h1>
              Minerva AI
              <br />
              Dove l'innovazione
              <br />
              incontra l'educazione
            </h1>

            <p>
            Un nuovo strumento per insegnare, pensato per chi vuole fare la differenza: flessibile, creativo, potente.
            </p>

            {/* Renderizzazione condizionale del pulsante */}
            {!isLoggedIn && (
              <a href="/welcomepage" className="try-minerva-button">
                Inizia Gratis
              </a>
            )}

            {/* Inserisci il nuovo componente Presentation */}
            <Presentation />
          </motion.section>

          {/* Sezione 2 */}
          <motion.section
            className="section-two"
            initial="hidden"
            animate="visible"
            variants={sectionVariants}
          >
            <div className="text-content">
              <img
                src="/logo/logogrey.svg"
                alt="Logo"
                className="section-two-logo"
              />
              <h2>L'assistente ideale per insegnare.</h2>
              <p>
              Minerva AI Education ti supporta dove le giornate non bastano: crea idee,
                laboratori e piani didattici in pochi istanti, integrando
                tecniche didattiche innovative e personalizzate.
              </p>
            </div>
            <div className="image-content">
              <div className="gradient-box" />
              <motion.img
                src="/home_icon/child.png"
                alt="Bambino"
                className="child-image"
                initial={{ x: '100%', opacity: 0 }}
                animate={{ x: '0%', opacity: 1 }}
                transition={{ duration: 1 }}
              />
            </div>
          </motion.section>

          {/* Sezione Servizi */}
          <ServicesComponent />

          {/* Sezione Benefici */}
          <BenefitsComponent />

          {/* Sezione Newsletter */}
          <motion.section
            className="newsletter-section"
            initial="hidden"
            animate="visible"
            variants={sectionVariants}
          >
            <div className="newsletter-container">
              <h2>Rimani aggiornato con Minerva</h2>
              <p>Iscriviti alla nostra newsletter per ricevere aggiornamenti, risorse didattiche gratuite e consigli per innovare la tua didattica con l'intelligenza artificiale.</p>
              
              <div className="newsletter-form">
                <NewsletterSignup />
              </div>
            </div>
          </motion.section>
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default HomePage;
